import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from 'styled-components';
import { LogOut } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useAppContext } from '../appprovider';
import {
    Sheet,
    Head,
    Chip,
    Title,
    Sub,
    Foot,
    FootLink,
    CloseWrap,
    activationCodeDialogPaperProps,
    getPlanTierDialogBackdropProps,
} from './ui/planTierPresentation';

/**
 * Read-only, dismissible plan-expired notice for non-owner org staff
 * (admins & instructors). They cannot renew billing themselves — only the
 * organisation owner can — so this explains the paused access and points them
 * to the owner, rather than the silent bounce they used to get. Owners keep the
 * full plan-selection dialog (MandatoryPackageDialog); this is intentionally
 * dismissible so it never blocks staff who can't act on it.
 */
export default function PlanExpiredNoticeDialog({ open, expiredDate, onDismiss }) {
    const theme = useTheme();
    const navigate = useNavigate();
    const { signOut } = useAuth();
    const { setUserId, setPackageDetails } = useAppContext();

    const handleLogout = async () => {
        try {
            setUserId(null);
            setPackageDetails(null);
            await signOut();
            navigate('/login', { replace: true });
        } catch (error) {
            console.error('Error during logout:', error);
        }
    };

    const expiryParsed = expiredDate ? new Date(expiredDate) : null;
    const hasExpiry = !!(expiryParsed && !isNaN(expiryParsed.getTime()));
    const expiredDateLabel = hasExpiry
        ? expiryParsed.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })
        : '';

    return (
        <Dialog
            open={open}
            onClose={() => { if (onDismiss) onDismiss(); }}
            maxWidth={false}
            PaperProps={activationCodeDialogPaperProps}
            slotProps={{ backdrop: getPlanTierDialogBackdropProps(theme) }}
        >
            <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
                <Sheet>
                    {onDismiss && (
                        <CloseWrap>
                            <IconButton size="small" aria-label="Close" onClick={onDismiss}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </CloseWrap>
                    )}
                    <Head>
                        <Chip $expired>
                            <span className="dot" />
                            {hasExpiry ? <>Subscription expired · {expiredDateLabel}</> : 'Subscription expired'}
                        </Chip>
                        <Title>Your organisation's plan has expired</Title>
                        <Sub>
                            Access to courses, assessments, and learner tools is paused until the plan is
                            renewed. Only your <b>organisation owner</b> can renew the plan — please reach out
                            to them to restore access. All your courses and data are safe.
                        </Sub>
                    </Head>

                    <Foot>
                        <FootLink onClick={onDismiss}>Maybe later</FootLink>
                        <FootLink onClick={handleLogout}><LogOut size={13} />Sign out</FootLink>
                    </Foot>
                </Sheet>
            </DialogContent>
        </Dialog>
    );
}
